/**
 * Plan Invoice — field-level effects.
 *
 * Migrated from: plan/plan-invoice-base.coffee
 *
 * Old validators → new effects:
 *   CopyClaimLine                     → CopyClaimLine
 *   CalculatePM                       → CalculatePM
 *   CalculateClaimAmount              → CalculateClaimAmount
 *   CalculateDueDate                  → CalculateDueDate
 *   CalculateCommission               → CalculateCommission
 *   CalculatePassThroughAndCommission → CalculatePassThroughAndCommission
 *
 * Skipped:
 *   handle(dfd) init logic — hides subform buttons, update_dispense_ids_dropdown
 *   open_dispense / open_adjustment — Backbone flyout events
 *   delete_row — recalculation via CalculateClaimAmount on dispense/adjustment
 *   add_data_rows — migrated as internal helper
 *   get_patient_info — migrated as internal helper
 *   show_termed_table — old modal alert, converted to field feedback
 *   Plan_invoiceCardFormView — Backbone card view (tab_do_save → Tier 3 formValidator)
 *   Plan_invoiceCardReadBaseView.print — print functionality not applicable
 */
import dayjs from "dayjs";

import { buildApiUrl, fetchAPI } from "@neu/libui";
import type { FieldHandler } from "@neu/libui";

function getListRows(data: any): any[] {
	return Array.isArray(data) ? data : (data?.data ?? []);
}

function getRelationID(value: any): number | undefined {
	if (value === null || value === undefined || value === "") {
		return undefined;
	}
	if (typeof value === "number") {
		return Number.isNaN(value) ? undefined : value;
	}
	if (typeof value === "string") {
		const parsed = parseInt(value, 10);
		return Number.isNaN(parsed) ? undefined : parsed;
	}
	if (typeof value === "object" && "id" in value) {
		return getRelationID(value.id);
	}
	return undefined;
}

function getRelationIDs(values: any): number[] {
	if (!Array.isArray(values)) return [];
	return values
		.map((value) => getRelationID(value))
		.filter((value): value is number => value !== undefined);
}

function getNumber(value: any): number {
	const parsed = parseFloat(String(value ?? ""));
	return Number.isNaN(parsed) ? 0 : parsed;
}

function getInteger(value: any): number {
	const parsed = parseInt(String(value ?? ""), 10);
	return Number.isNaN(parsed) ? 0 : parsed;
}

function formatCurrency(value: any): string {
	return getNumber(value).toFixed(2);
}

function getInvoiceSplitBy(value: any): string {
	if (Array.isArray(value)) {
		return value.includes("Secondary") ? "Secondary" : "Primary";
	}
	return value || "Primary";
}

function get340BFeeForInvoice(
	type: unknown,
	provider: unknown,
	fee340b: unknown
): string {
	if (type !== "340B") return "";
	if (provider !== "Hudson" && provider !== "ODA") return "";
	return formatCurrency(fee340b);
}

async function get340BFeeByDispenseIds(
	dispenseIds: number[]
): Promise<Map<number, string>> {
	const result = new Map<number, string>();
	const uniqueIds = [...new Set(dispenseIds.filter((id) => id > 0))];
	if (uniqueIds.length === 0) return result;

	try {
		const data = await fetchAPI(
			buildApiUrl(
				`/patient_order_dispense/list?limit=${uniqueIds.length}&_id=in:${uniqueIds.join(",")}&_auto_name=ex:true`
			)
		);
		for (const row of getListRows(data)) {
			const dispenseId = getRelationID(row.id);
			if (!dispenseId) continue;
			result.set(
				dispenseId,
				get340BFeeForInvoice(
					row.type,
					row.is_provider_available,
					row.fee_340b
				)
			);
		}
	} catch (err) {
		console.error("get340BFeeByDispenseIds: API error", err);
	}

	return result;
}

async function hydrateDispenseFunding(form: any) {
	const dispenseRows: any[] = form.getFieldState("dispense")?.value || [];
	const missingFundingRows = dispenseRows.filter(
		(row) =>
			row?.meta && (row.funding === undefined || row.funding === null)
	);

	if (missingFundingRows.length === 0) return;

	const ids = [
		...new Set(getRelationIDs(missingFundingRows.map((row) => row.meta))),
	];
	if (ids.length === 0) return;

	const data = await fetchAPI(
		buildApiUrl(
			`/plan_claim_dispense/list?limit=${ids.length}&_id=in:${ids.join(",")}`
		)
	);
	const claimRows = getListRows(data);
	const fundingById = new Map<number, string>();

	for (const row of claimRows) {
		const rowId = getRelationID(row.id);
		if (!rowId) continue;
		fundingById.set(rowId, formatCurrency(row.funding_amount));
	}

	form.setFieldState("dispense", (s: any) => {
		const currentRows: any[] = s.value || [];
		s.value = currentRows.map((row) => {
			const rowId = getRelationID(row?.meta);
			if (!rowId || !fundingById.has(rowId)) return row;
			return {
				...row,
				funding: fundingById.get(rowId),
			};
		});
	});
}

function isActiveInvoiceRow(row: any): boolean {
	return row?.deleted !== true && row?.archived !== true;
}

function getInvoiceTotals(form: any) {
	let amt = 0;
	let fundingAmt = 0;
	let feeAmt = 0;
	let adjustmentAmt = 0;
	const billingType = form.getFieldState("billing_type")?.value;

	for (const tableName of ["dispense", "adjustment"]) {
		const rows: any[] = form.getFieldState(tableName)?.value || [];
		for (const row of rows) {
			if (!isActiveInvoiceRow(row)) continue;
			if (!row?.fee && !row?.amount) continue;
			if (tableName === "dispense") {
				fundingAmt += getNumber(row.funding);
				feeAmt += getNumber(row.fee);
				if (!["PMPM", "PEPM"].includes(billingType)) {
					amt += getNumber(row.fee);
				}
				continue;
			}

			amt += getNumber(row.amount);
			adjustmentAmt += getNumber(row.amount);
		}
	}

	if (billingType === "PMPM") {
		amt +=
			getNumber(form.getFieldState("pmpm_fee")?.value) *
			getInteger(form.getFieldState("employees_num")?.value);
	} else if (billingType === "PEPM") {
		amt +=
			getNumber(form.getFieldState("pepm_fee")?.value) *
			getInteger(form.getFieldState("pe_employees_num")?.value);
	}

	return { amt, fundingAmt, feeAmt, adjustmentAmt };
}

async function calculateInvoiceAmounts(form: any, editID?: number | string) {
	await hydrateDispenseFunding(form);

	const { amt, fundingAmt, feeAmt, adjustmentAmt } = getInvoiceTotals(form);

	form.setFieldState("amount", (s: any) => {
		s.value = amt;
	});
	try {
		form.setFieldState("funding_amount", (s: any) => {
			s.value = fundingAmt;
		});
	} catch {
		// funding_amount may not exist
	}
	form.setFieldState("fee_amount", (s: any) => {
		s.value = feeAmt;
	});
	form.setFieldState("adjustment_amount", (s: any) => {
		s.value = adjustmentAmt;
	});

	const fundingYtd = getNumber(
		form.getFieldState("funding_amount_ytd")?.value
	);
	if (fundingYtd > 0) return;

	const planId = getRelationID(form.getFieldState("plan_id")?.value);
	if (!planId) {
		form.setFieldState("funding_amount_ytd", (s: any) => {
			s.value = fundingAmt;
		});
		return;
	}

	try {
		let url = `/plan_invoice/list?_plan_id=eq:${planId}&sort=-date&limit=1`;
		if (editID) url += `&_id=ne:${editID}`;
		const data = await fetchAPI(buildApiUrl(url));
		const rows = getListRows(data);

		form.setFieldState("funding_amount_ytd", (s: any) => {
			s.value =
				rows.length > 0
					? getNumber(rows[0].funding_amount_ytd) + fundingAmt
					: fundingAmt;
		});
	} catch (err) {
		console.error("calculateInvoiceAmounts: API error", err);
		form.setFieldState("funding_amount_ytd", (s: any) => {
			s.value = fundingAmt;
		});
	}
}

function calculateRxPassThrough(form: any, plan: any) {
	let rxTotal = 0;
	const pbms = Array.isArray(plan?.sf_plan_pbm) ? plan.sf_plan_pbm : [];

	for (const pbm of pbms) {
		if (pbm?.use_in_invoice !== "Yes") continue;
		rxTotal += getNumber(pbm?.pbm_amount);
	}

	form.setFieldState("rx_total", (s: any) => {
		s.value = rxTotal;
	});
}

async function getPatientsByIds(patientIds: number[]) {
	if (patientIds.length === 0) {
		return new Map<number, any>();
	}

	const uniqueIds = [...new Set(patientIds)];
	const data = await fetchAPI(
		buildApiUrl(
			`/patient/list?limit=${uniqueIds.length}&_id=in:${uniqueIds.join(",")}`
		)
	);
	const rows = getListRows(data);

	return new Map<number, any>(
		rows
			.map((row: any) => [getRelationID(row.id), row] as const)
			.filter(
				(entry): entry is readonly [number, any] =>
					entry[0] !== undefined
			)
	);
}

function setTermedFeedback(field: any, termedPatients: string[]) {
	if (termedPatients.length > 0) {
		field.setFeedback({
			type: "warning",
			messages: [
				`This invoice contains dispenses for termed members: ${termedPatients.join(", ")}`,
			],
		});
		return;
	}

	field.setFeedback({ type: "warning", messages: [] });
}

// ─── CalculateClaimAmount ──────────────────────────────────────────────────
// Old: DSLFx.Validators.CalculateClaimAmount → handler.calculate_claim_amount
export const CalculateClaimAmount: FieldHandler = (field, { form, editID }) => {
	void (async () => {
		await calculateInvoiceAmounts(form, editID);
	})();
	return "";
};

// ─── CalculatePM ───────────────────────────────────────────────────────────
// Old: DSLFx.Validators.CalculatePM → handler.calc_pm
export const CalculatePM: FieldHandler = (field, ctx) => {
	const billingType = ctx.form.getFieldState("billing_type")?.value;
	if (!["PEPM", "PMPM"].includes(billingType)) return "";
	return CalculateClaimAmount(field, ctx);
};

// ─── CalculateDueDate ──────────────────────────────────────────────────────
// Old: DSLFx.Validators.CalculateDueDate → handler.calculate_due_date
export const CalculateDueDate: FieldHandler = (field, { form }) => {
	void (async () => {
		const planId = getRelationID(form.getFieldState("plan_id")?.value);
		if (!planId) {
			form.setFieldState("due_date", (s) => {
				s.value = dayjs().format("YYYY-MM-DD");
			});
			return;
		}

		try {
			const data = await fetchAPI(buildApiUrl(`/plan/${planId}`));
			const plan = Array.isArray(data) ? data[0] : data;
			if (!plan) {
				form.setFieldState("due_date", (s) => {
					s.value = dayjs().format("YYYY-MM-DD");
				});
				return;
			}

			const netDays = getInteger(plan.net_amount) || 30;
			const invoiceDate = form.getFieldState("date")?.value;
			form.setFieldState("due_date", (s) => {
				s.value = dayjs(invoiceDate)
					.add(netDays, "day")
					.format("YYYY-MM-DD");
			});

			if (!form.getFieldState("billing_type")?.value) {
				form.setFieldState("billing_type", (s) => {
					s.value = plan.billing_type;
				});
			}
		} catch (err) {
			console.error("CalculateDueDate: API error", err);
			form.setFieldState("due_date", (s) => {
				s.value = dayjs().format("YYYY-MM-DD");
			});
		}
	})();
	return "";
};

// ─── CalculateCommission ───────────────────────────────────────────────────
// Old: DSLFx.Validators.CalculateCommission → handler.calculate_commission
export const CalculateCommission: FieldHandler = (field, { form }) => {
	void (async () => {
		const closeoutId = form.getFieldState("closeout_id")?.value;
		const invoiceDate = form.getFieldState("date")?.value;
		if (closeoutId || !invoiceDate) return;

		form.setFieldState("broker_commissions", (s) => {
			s.value = [];
		});
		form.setFieldState("commission_amount", (s) => {
			s.value = 0;
		});

		const planId = getRelationID(form.getFieldState("plan_id")?.value);
		if (!planId) return;

		try {
			const data = await fetchAPI(
				buildApiUrl(
					`/bi/broker/plan_broker/${planId}/${dayjs(invoiceDate).format("YYYY-MM-DD")}`
				)
			);
			const brokers = getListRows(data);
			if (brokers.length === 0) return;

			const feeAmount = getNumber(
				form.getFieldState("fee_amount")?.value
			);
			const fundingAmount = getNumber(
				form.getFieldState("funding_amount")?.value
			);
			let totalCommission = 0;
			const commissionRows: any[] = [];

			for (const broker of brokers) {
				let billedPerc = 0;
				let costAvoidPerc = 0;

				if (
					(broker.broker_commissionable_base ===
						"Billed Charges and Cost Avoidance" ||
						broker.broker_commissionable_base ===
							"Billed Charges") &&
					getNumber(broker.broker_commissionable_bill_perc) > 0
				) {
					billedPerc =
						(getNumber(broker.broker_commissionable_bill_perc) /
							100) *
						feeAmount;
				}

				if (
					(broker.broker_commissionable_base ===
						"Billed Charges and Cost Avoidance" ||
						broker.broker_commissionable_base ===
							"Cost Avoidance") &&
					getNumber(broker.broker_commissionable_cost_perc) > 0
				) {
					costAvoidPerc =
						(getNumber(broker.broker_commissionable_cost_perc) /
							100) *
						fundingAmount;
				}

				const total = billedPerc + costAvoidPerc;
				if (total <= 0) continue;

				const brokerId = getInteger(broker.broker_id);
				if (
					commissionRows.some(
						(row) => getInteger(row.broker_id) === brokerId
					)
				) {
					continue;
				}

				totalCommission += total;
				commissionRows.push({
					broker_id: brokerId,
					broker_name: broker.broker,
					broker_bill_perc: formatCurrency(billedPerc),
					broker_cost_avoid_perc: formatCurrency(costAvoidPerc),
					total_commission: formatCurrency(total),
				});
			}

			form.setFieldState("broker_commissions", (s) => {
				s.value = commissionRows;
			});
			form.setFieldState("commission_amount", (s) => {
				s.value = totalCommission;
			});
		} catch (err) {
			console.error("CalculateCommission: API error", err);
		}
	})();
	return "";
};

// ─── CalculatePassThroughAndCommission ─────────────────────────────────────
// Old: DSLFx.Validators.CalculatePassThroughAndCommission
//      → handler.calculate_pass_through_and_commission
export const CalculatePassThroughAndCommission: FieldHandler = (
	field,
	{ form }
) => {
	void (async () => {
		const dispensesList: any[] = (
			form.getFieldState("dispense")?.value || []
		).filter(isActiveInvoiceRow);

		form.setFieldState("pass_through_fee", (s) => {
			s.value = 0;
		});
		form.setFieldState("commission_fee", (s) => {
			s.value = 0;
		});
		form.setFieldState("rx_total", (s) => {
			s.value = 0;
		});

		if (dispensesList.length === 0) return;

		const planId = getRelationID(form.getFieldState("plan_id")?.value);
		if (!planId) return;

		try {
			const data = await fetchAPI(buildApiUrl(`/plan/${planId}`));
			const plan = Array.isArray(data) ? data[0] : data;
			if (!plan) return;

			calculateRxPassThrough(form, plan);

			const descOrderedDispenses = [...dispensesList].sort(
				(a: any, b: any) =>
					dayjs(b.filled).valueOf() - dayjs(a.filled).valueOf()
			);

			let totalCommission = 0;
			let totalPassThrough = 0;

			if (plan.commission_fee_active === "Yes") {
				const schedule = Array.isArray(plan.commission_fee)
					? plan.commission_fee
					: [];
				for (const dispense of descOrderedDispenses) {
					const dispenseDate = dayjs(dispense.filled);
					const match = schedule.find((row: any) => {
						const startDate = dayjs(row.start_date);
						const endDate = row.end_date
							? dayjs(row.end_date)
							: null;
						return (
							dispenseDate.valueOf() >= startDate.valueOf() &&
							(!endDate ||
								dispenseDate.valueOf() <= endDate.valueOf())
						);
					});
					if (!match) continue;

					totalCommission += getNumber(
						(
							(getNumber(match.commission_fee_percent) *
								getNumber(dispense.fee)) /
							100
						).toFixed(2)
					);
				}

				form.setFieldState("commission_fee", (s) => {
					s.value = totalCommission;
				});
			}

			if (plan.pass_through_fee_active === "Yes") {
				const schedule = Array.isArray(plan.pass_through_fee)
					? plan.pass_through_fee
					: [];
				for (const dispense of descOrderedDispenses) {
					const dispenseDate = dayjs(dispense.filled);
					const match = schedule.find((row: any) => {
						const startDate = dayjs(row.start_date);
						const endDate = row.end_date
							? dayjs(row.end_date)
							: null;
						return (
							dispenseDate.valueOf() >= startDate.valueOf() &&
							(!endDate ||
								dispenseDate.valueOf() <= endDate.valueOf())
						);
					});
					if (!match) continue;

					totalPassThrough += getNumber(
						(
							(getNumber(match.pass_through_fee_percent) *
								getNumber(dispense.fee)) /
							100
						).toFixed(2)
					);
				}

				form.setFieldState("pass_through_fee", (s) => {
					s.value = totalPassThrough;
				});
			}
		} catch (err) {
			console.error("CalculatePassThroughAndCommission: API error", err);
		}
	})();
	return "";
};

// ─── CopyClaimLine ─────────────────────────────────────────────────────────
// Old: DSLFx.Validators.CopyClaimLine → handler.copy_lineitem
export const CopyClaimLine: FieldHandler = (field, { form, editID }, props) => {
	void (async () => {
		const formType = (props?.form as string) || "dispense";
		const triggerField =
			formType === "dispense" ? "auto_dispenses" : "auto_adjustments";
		const value = form.getFieldState(triggerField)?.value;
		const planId = getRelationID(form.getFieldState("plan_id")?.value);

		if (!planId || value !== "Yes") return;

		setTimeout(() => {
			form.setFieldState(triggerField, (s) => {
				s.value = "No";
			});
		}, 250);

		if (formType === "dispense") {
			form.setFieldState("dispense", (s) => {
				s.value = [];
			});
		}

		try {
			const billingType = form.getFieldState("billing_type")?.value;
			const groupSplit = form.getFieldState("group_split")?.value || "No";

			if (
				groupSplit !== "No" &&
				formType === "dispense" &&
				billingType !== "PMPM"
			) {
				const planGroups = getRelationIDs(
					form.getFieldState("plan_groups")?.value
				);
				if (planGroups.length > 0) {
					const groupIds = planGroups.join(",");
					const splitBy = getInvoiceSplitBy(
						form.getFieldState("invoice_split_by")?.value
					);
					const url = editID
						? `/bi/plan_group/plan_group_dispenses/${planId}/${editID}?group_ids=${groupIds}&split_by=${splitBy}`
						: `/bi/plan_group/plan_group_dispenses/${planId}/null?group_ids=${groupIds}&split_by=${splitBy}`;
					const data = await fetchAPI(buildApiUrl(url));
					const { termedPatients } = await addInvoiceDataRows(
						form,
						getListRows(data),
						formType,
						editID
					);
					setTermedFeedback(field, termedPatients);
					return;
				}
			}

			const termedNames = new Set<string>();

			const data = await fetchAPI(
				buildApiUrl(
					`/plan_claim_${formType}/list?limit=9999&_invoice_id=eq:null&_plan_id=eq:${planId}&_auto_name=ex:true`
				)
			);
			const firstPass = await addInvoiceDataRows(
				form,
				getListRows(data),
				formType,
				editID
			);
			for (const name of firstPass.termedPatients) {
				termedNames.add(name);
			}

			if (editID) {
				try {
					const data2 = await fetchAPI(
						buildApiUrl(
							`/plan_claim_${formType}/list?limit=9999&_invoice_id=eq:${editID}&_plan_id=eq:${planId}&_auto_name=ex:true`
						)
					);
					const secondPass = await addInvoiceDataRows(
						form,
						getListRows(data2),
						formType,
						editID
					);
					for (const name of secondPass.termedPatients) {
						termedNames.add(name);
					}
				} catch (err) {
					console.error(
						"CopyClaimLine: error fetching invoiced items",
						err
					);
				}
			}

			setTermedFeedback(field, [...termedNames]);
		} catch (err) {
			console.error("CopyClaimLine: API error", err);
		}
	})();
	return "";
};

async function addInvoiceDataRows(
	form: any,
	data: any[],
	formType: string,
	editID?: number | string
) {
	const invoiceDate = form.getFieldState("date")?.value;
	const lastMonth = invoiceDate
		? dayjs(invoiceDate).subtract(1, "month").endOf("month")
		: dayjs().subtract(1, "month").endOf("month");

	const startDate =
		form.getFieldState("dispense_start_date")?.value || "1899-01-01";
	const endDate =
		form.getFieldState("dispense_end_date")?.value || "3000-01-01";
	const startAmount = getNumber(
		form.getFieldState("dispense_start_amount")?.value
	);
	const endAmount =
		getNumber(form.getFieldState("dispense_end_amount")?.value) || 9999999;
	const startFee = getNumber(form.getFieldState("dispense_start_fee")?.value);
	const endFee =
		getNumber(form.getFieldState("dispense_end_fee")?.value) || 9999999;
	const dispenseIds = getRelationIDs(
		form.getFieldState("pc_dispense_ids")?.value
	);
	const patientIds = getRelationIDs(form.getFieldState("member_name")?.value);
	const termedPatients = new Set<string>();

	if (formType === "dispense") {
		const existingRows: any[] = form.getFieldState("dispense")?.value || [];
		const existingIds = new Set(
			existingRows
				.map((row) => getRelationID(row.meta ?? row._meta))
				.filter((value): value is number => value !== undefined)
		);
		const newRows = [...existingRows];
		const patientsById = await getPatientsByIds(
			data
				.map((row) => getRelationID(row?.patient_id))
				.filter((value): value is number => value !== undefined)
		);
		const fee340bByDispenseId = await get340BFeeByDispenseIds(
			data
				.map((row) => getInteger(row?.original_dispense_id))
				.filter((value) => value > 0)
		);

		for (const row of data) {
			const rowId = getRelationID(row.id);
			const patientId = getRelationID(row.patient_id);
			if (!rowId || existingIds.has(rowId)) continue;
			if (dispenseIds.length > 0 && !dispenseIds.includes(rowId)) {
				continue;
			}
			if (
				patientIds.length > 0 &&
				(!patientId || !patientIds.includes(patientId))
			) {
				continue;
			}
			if (dayjs(row.dispense_date).isBefore(startDate)) continue;
			if (dayjs(row.dispense_date).isAfter(endDate)) continue;
			if (!row.original_dispense_id) continue;
			if (getNumber(row.claim_amount) < startAmount) continue;
			if (getNumber(row.claim_amount) > endAmount) continue;
			if (getNumber(row.fee_amount) < startFee) continue;
			if (getNumber(row.fee_amount) > endFee) continue;
			if (lastMonth.diff(dayjs(row.dispense_date)) < 0) continue;
			if (!patientId) continue;

			const patient = patientsById.get(patientId);
			if (!patient) continue;
			if (patient.archived === true || patient.deleted === true) continue;

			const patientDisplayName =
				[patient.lastname, patient.firstname]
					.filter(Boolean)
					.join(", ") ||
				patient.auto_name ||
				"";
			if (patient.status === "Account Terminated" && patientDisplayName) {
				termedPatients.add(patientDisplayName);
			}

			const originalDispenseId = getInteger(row.original_dispense_id);
			const fee340bFromDispense =
				fee340bByDispenseId.get(originalDispenseId) ?? "";
			const fee_340b =
				fee340bFromDispense ||
				(row.fee_340b !== undefined &&
				row.fee_340b !== null &&
				row.fee_340b !== ""
					? formatCurrency(row.fee_340b)
					: "");

			newRows.push({
				meta: rowId,
				original_dispense_id: originalDispenseId,
				type: row.type,
				fee_340b,
				member_id: row.member_id,
				member_name: patientDisplayName,
				drug: row.drug_name,
				filled: row.dispense_date,
				funding: formatCurrency(row.funding_amount),
				fee: formatCurrency(row.fee_amount),
			});
		}

		form.setFieldState("dispense", (s: any) => {
			s.value = newRows;
		});
	} else {
		const existingRows: any[] =
			form.getFieldState("adjustment")?.value || [];
		const existingIds = new Set(
			existingRows
				.map((row) => getRelationID(row.meta ?? row._meta))
				.filter((value): value is number => value !== undefined)
		);
		const newRows = [...existingRows];

		for (const row of data) {
			const rowId = getRelationID(row.id);
			if (!rowId || existingIds.has(rowId)) continue;
			newRows.push({
				meta: rowId,
				date: row.date,
				type: row.type,
				amount: formatCurrency(row.amount),
				comment: row.comment,
			});
		}

		form.setFieldState("adjustment", (s: any) => {
			s.value = newRows;
		});
	}

	await calculateInvoiceAmounts(form, editID);
	return { termedPatients: [...termedPatients] };
}
